import React, { useEffect, useRef, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner } from '../ui.jsx';

// Loads the Twilio Voice JS SDK from CDN on first use.
function loadVoiceSdk() {
  return new Promise((resolve, reject) => {
    if (window.Twilio?.Device) return resolve(window.Twilio);
    // Modern Twilio Voice SDK 2.x via jsdelivr (the only working CDN for v2).
    // API: new Twilio.Device(token, opts); await device.connect({ params: {...} }).
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/@twilio/voice-sdk@2.10.0/dist/twilio.min.js';
    s.onload = () => {
      if (window.Twilio?.Device) resolve(window.Twilio);
      else reject(new Error('Twilio SDK loaded but Device not available'));
    };
    s.onerror = () => reject(new Error('Failed to load Twilio Voice SDK from CDN'));
    document.head.appendChild(s);
  });
}

// Global controller for the dialer. Mount once at the app root; any component fires
// `tradeops:voip-call` with `{ to, callerId, name }` to dial out.
export function VoipDialerProvider({ user, toast }) {
  const [callState, setCallState] = useState(null); // null | 'connecting' | 'ringing' | 'in-call' | 'error'
  const [activeNumber, setActiveNumber] = useState('');
  const [activeName, setActiveName] = useState('');
  const [activeCallerId, setActiveCallerId] = useState('');
  const [muted, setMuted] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const deviceRef = useRef(null);
  const connRef = useRef(null);

  // Tick the elapsed timer while in-call
  useEffect(() => {
    if (callState !== 'in-call' || !startedAt) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callState, startedAt]);

  const initDevice = async () => {
    if (deviceRef.current) return deviceRef.current;
    const Twilio = await loadVoiceSdk();
    const r = await api('/api/messaging/voip-token', { method: 'POST' });
    if (!r.token) throw new Error('No VOIP token');
    // Twilio Voice SDK 2.x: new Device(token, opts) — proper instance-based API.
    const device = new Twilio.Device(r.token, {
      codecPreferences: ['opus', 'pcmu'],
      logLevel: 1,
    });
    device.on('error', (e) => {
      // Twilio Voice SDK errors carry { code, message, description, causes, solutions }
      const code = e?.code || e?.name || 'unknown';
      const msg = e?.message || e?.description || String(e);
      const detail = `${code}: ${msg}`;
      console.warn('[VOIP device error]', code, msg, e);
      setErrorMsg(detail);
      setCallState('error');
      toast?.(`Call error ${detail}`, 'error');
    });
    device.on('disconnect', () => {
      setCallState(null);
      setStartedAt(null);
      setElapsed(0);
      connRef.current = null;
    });
    device.on('connect', () => {
      setCallState('in-call');
      setStartedAt(Date.now());
    });
    deviceRef.current = device;
    return device;
  };

  const placeCall = async ({ to, callerId, name }) => {
    if (!to) return;
    setActiveNumber(to);
    setActiveName(name || '');
    setActiveCallerId(callerId || '');
    setCallState('connecting');
    setMuted(false);
    setErrorMsg('');
    try {
      const device = await initDevice();
      // SDK 2.x: device.connect({params: {...}}) is async, returns a Call.
      const conn = await device.connect({
        params: { To: to, CallerId: callerId || '' },
      });
      connRef.current = conn;
      // Per-call error listener — catches issues that happen mid-call (TwiML rejected, etc).
      conn.on?.('error', (e) => {
        const detail = `${e?.code || 'err'}: ${e?.message || e?.description || e}`;
        console.warn('[VOIP conn error]', e);
        setErrorMsg(detail);
        setCallState('error');
        toast?.(`Call failed ${detail}`, 'error');
      });
      conn.on?.('disconnect', () => {
        // Normal hangup or carrier hangup
        setCallState((s) => s === 'error' ? s : null);
      });
      setCallState('ringing');
    } catch (e) {
      const detail = `${e?.code || 'init'}: ${e?.message || e}`;
      setErrorMsg(detail);
      setCallState('error');
      toast?.(`Could not start call: ${detail}`, 'error');
    }
  };

  const hangup = () => {
    try {
      // SDK 2.x: call.disconnect() OR device.disconnectAll()
      connRef.current?.disconnect?.();
      deviceRef.current?.disconnectAll?.();
    } catch {}
    setCallState(null);
    setStartedAt(null);
    setElapsed(0);
  };

  const toggleMute = () => {
    const next = !muted;
    try {
      // 1.x: connection.mute(true/false). 2.x: connection.mute(true/false) — same API.
      if (connRef.current?.mute) connRef.current.mute(next);
      setMuted(next);
    } catch {}
  };

  // Listen for global call requests fired by any screen
  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      placeCall({ to: d.to, callerId: d.callerId, name: d.name });
    };
    window.addEventListener('tradeops:voip-call', handler);
    return () => window.removeEventListener('tradeops:voip-call', handler);
  }, []);

  if (!callState) return null;

  const stateLabel = {
    connecting: 'Connecting...',
    ringing: 'Ringing...',
    'in-call': formatElapsed(elapsed),
    error: errorMsg ? `Failed — ${errorMsg}` : 'Failed',
  }[callState] || '';

  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:1000,
      background:G.card, border:`1px solid ${G.purple+'66'}`, borderRadius:14,
      padding:'16px 20px', minWidth:300, boxShadow:'0 16px 40px rgba(0,0,0,0.5)',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
        <div style={{ width:42, height:42, borderRadius:99, background:G.purple+'22', color:G.purple, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
          {callState === 'in-call' ? '📞' : '📲'}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:15, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {activeName || activeNumber}
          </div>
          <div style={{ fontSize:12, color:G.textMuted }}>
            {activeName && activeNumber} {activeName ? '· ' : ''}{stateLabel}
          </div>
        </div>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        {callState === 'in-call' && (
          <Btn sz="sm" onClick={toggleMute}>{muted ? '🔊 Unmute' : '🔇 Mute'}</Btn>
        )}
        <Btn sz="sm" v="danger" onClick={hangup} style={{ flex:1 }}>
          {callState === 'in-call' ? '📵 Hang up' : 'Cancel'}
        </Btn>
      </div>
    </div>
  );
}

function formatElapsed(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2,'0')}`;
}

// Convenience helper any screen can call: <button onClick={()=>voipCall(...)}>
export function voipCall({ to, callerId, name }) {
  if (!to) return;
  try { window.dispatchEvent(new CustomEvent('tradeops:voip-call', { detail: { to, callerId, name }})); } catch {}
}
